import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaMoneyBillWave, FaCalendarCheck, FaBook, FaClipboardList, FaBus, FaGraduationCap, FaBookOpen, FaPencilRuler } from "react-icons/fa";
import { studentService } from "../../services/studentService";
import toast from "react-hot-toast";

function ProfilePage() {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchStudentData();
  }, []);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      const studentId = localStorage.getItem("selectedStudentId");
      if (!studentId) {
        toast.error("No student selected");
        setLoading(false);
        return;
      }

      const response = await studentService.getStudentEnrollment(studentId);
      const enrollmentData = Array.isArray(response.data) ? response.data[0] : response.data;

      if (!enrollmentData) {
        toast.error("No enrollment data found");
        setLoading(false);
        return;
      }

      const formattedStudent = {
        id: enrollmentData.student?._id || enrollmentData._id,
        name: enrollmentData.student?.name || enrollmentData.student?.firstName || "N/A",
        regNo: enrollmentData?.uniqueRollNo || "N/A",
        phone: enrollmentData.student?.contactNumber || "N/A",
        email: enrollmentData.student?.email || "N/A",
        class: enrollmentData.class?.name || "N/A",
        rollNo: enrollmentData?.rollno || "N/A",
        section: enrollmentData.section || enrollmentData.student?.section || "A",
        academicSession: enrollmentData.academicSession?.label || "2024-2025",
        parentsName: enrollmentData.student?.fatherName || "N/A",
        imageUrl: enrollmentData.student?.profilePicture || enrollmentData.student?.profileImage || "/un.png",
      };

      setStudent(formattedStudent);
    } catch (error) {
      console.error("Error fetching student profile:", error);
      toast.error("Failed to load student profile");
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    {
      title: "Payments",
      icon: <FaMoneyBillWave className="text-3xl mb-2" />,
      link: "/payments",
      disabled: false,
      color: "text-pragyaBlue",
      bgColor: "bg-pragyaBlue/5 hover:bg-pragyaBlue/10",
    },
    {
      title: "Attendance",
      icon: <FaCalendarCheck className="text-3xl mb-2" />,
      link: "/attendance",
      disabled: false,
      color: "text-orange-500",
      bgColor: "bg-orange-50 hover:bg-orange-100/50",
    },
    {
      title: "Assignments",
      icon: <FaClipboardList className="text-3xl mb-2" />,
      link: "/assignments",
      disabled: false,
      color: "text-purple-500",
      bgColor: "bg-purple-50 hover:bg-purple-100/50",
    },
    {
      title: "Examinations",
      icon: <FaGraduationCap className="text-3xl mb-2" />,
      link: "/exam-schedule",
      disabled: false,
      color: "text-red-500",
      bgColor: "bg-red-50 hover:bg-red-100/50",
    },
    {
      title: "Library",
      icon: <FaBook className="text-3xl mb-2" />,
      link: "/library",
      disabled: false,
      color: "text-blue-500",
      bgColor: "bg-blue-50 hover:bg-blue-100/50",
    },
    {
      title: "Transport",
      icon: <FaBus className="text-3xl mb-2" />,
      link: "/transport",
      disabled: false,
      color: "text-amber-500",
      bgColor: "bg-amber-50 hover:bg-amber-100/50",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="relative w-32 h-32 mb-8">
          {/* Background Glow */}
          <motion.div
            className="absolute inset-0 bg-pragyaBlue/10 rounded-full blur-2xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Central Icon */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center bg-white rounded-3xl shadow-xl border border-slate-50 z-10"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <FaGraduationCap className="text-4xl text-pragyaBlue" />
          </motion.div>

          {/* Orbiting Icons */}
          <motion.div
            className="absolute -top-4 -right-4 w-10 h-10 bg-pragyaOrange/10 rounded-xl flex items-center justify-center text-pragyaOrange"
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          >
            <FaBookOpen />
          </motion.div>

          <motion.div
            className="absolute -bottom-2 -left-4 w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-600"
            animate={{ rotate: -360 }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          >
            <FaPencilRuler />
          </motion.div>
        </div>

        <div className="text-center">
          <motion.h3
            className="text-lg font-bold text-slate-800 mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Fetching Student Records
          </motion.h3>

          <div className="flex flex-col items-center gap-2">
            <div className="w-48 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-pragyaBlue"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
            <motion.p
              className="text-xs text-slate-400 font-medium"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Organizing your academic profile...
            </motion.p>
          </div>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-bold text-slate-800">Student not found</h2>
          <Link to="/dashboard" className="text-pragyaBlue mt-4 inline-block font-semibold">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-slate-50 p-4 pb-32"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Student Header Card */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 flex flex-col items-center text-center border border-slate-100">
        <div className="relative mb-4">
          <div className="absolute inset-0 bg-pragyaBlue/10 rounded-full blur-md animate-pulse"></div>
          <img
            src={student.imageUrl}
            alt="Profile"
            className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md relative z-10"
          />
        </div>
        <h2 className="text-xl font-bold text-slate-800">{student.name}</h2>
        <div className="flex flex-wrap justify-center gap-2 mt-2 text-sm text-slate-500">
          <span className="px-3 py-1 bg-slate-100 rounded-full">Class: {student.class}</span>
          <span className="px-3 py-1 bg-slate-100 rounded-full">Section: {student.section}</span>
          <span className="px-3 py-1 bg-slate-100 rounded-full">Roll No: {student.rollNo}</span>
        </div>
      </div>

      {/* Menu Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {menuItems.map((item, index) => (
          item.disabled ? (
            <div
              key={index}
              className={`flex flex-col items-center justify-center p-6 rounded-xl border border-slate-100 ${item.bgColor} cursor-not-allowed opacity-60 grayscale`}
            >
              <div className={item.color}>{item.icon}</div>
              <span className="font-medium text-slate-400">{item.title}</span>
            </div>
          ) : (
            <Link
              key={index}
              to={item.link}
              className={`flex flex-col items-center justify-center p-6 rounded-xl border border-slate-100 shadow-sm transition-all duration-200 transform hover:-translate-y-1 hover:shadow-md ${item.bgColor}`}
            >
              <div className={item.color}>{item.icon}</div>
              <span className="font-semibold text-slate-700">{item.title}</span>
            </Link>
          )
        ))}
      </div>
    </motion.div>
  );
}

export default ProfilePage;
